import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import { Clock, MapPin, Bell, User, LogOut, Home, ShoppingBag, Settings, Menu, X, CheckCircle, RefreshCw, XCircle, Phone, Mail, LucideIcon, Plus } from 'lucide-react';
import { format } from 'date-fns';
import SkeletonUserDashboard from './SkeletonUserDashboard';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

// Interfaces
interface MenuItem {
  id?: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  image_url?: string;
  category?: string;
  quantity: number;
}

interface AvailableMenuItem {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  category?: string;
}

interface MenuDetails {
  id?: string;
  user_id?: string;
  items: MenuItem[];
  total_amount: number;
  shipping_info?: { address?: string };
  payment_method?: string;
  status?: string;
  created_at?: string;
  ordersId?: string;
}

interface Order {
  id: string;
  typeoforder?: string;
  details?: any;
  created_at?: string;
  menuDetails?: MenuDetails;
  menuorder_id?: string | null;
  user_id?: string;
}

interface Profile {
  id?: string;
  full_name?: string;
  email?: string;
  created_at?: string;
  role?: string;
  phone?: string;
  address?: string;
  updated_at?: string;
}

interface Reservation {
  id: string;
  name: string;
  email?: string;
  contact: string;
  date: string;
  time: string;
  guests: number;
  special_requests?: string;
  created_at: string;
  status: string;
}

interface TakeawayOrder {
  id: string;
  name: string;
  email?: string;
  contact: string;
  pickup_time: string;
  address: string;
  menu_selections: MenuItem[] | null;
  instructions?: string;
  created_at: string;
  order_status: string;
}

interface PartyOrder {
  id: string;
  name: string;
  email?: string;
  contact: string;
  event_date: string;
  guest_count: number;
  dish_selections: MenuItem[] | null;
  delivery_method: string;
  special_requests?: string;
  created_at: string;
  status: string;
  address: string;
}

interface ModifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: MenuDetails;
  onSave: (updatedOrder: MenuDetails) => void;
  onCancelOrder: (orderId: string) => void;
}

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profile: { full_name: string; address: string; phone: string }) => void;
  initialData: { full_name: string; address: string; phone: string };
}

// Card Component
const Card: React.FC<{ title: string; children: React.ReactNode; orderType?: 'reservation' | 'takeaway' | 'party' | 'menu' | 'unknown' }> = ({ title, children, orderType = 'unknown' }) => {
  const headerColors: Record<string, string> = {
    reservation: 'bg-emerald-100 dark:bg-emerald-800',
    takeaway: 'bg-emerald-200 dark:bg-emerald-700',
    party: 'bg-emerald-300 dark:bg-emerald-600',
    menu: 'bg-emerald-400 dark:bg-emerald-500',
    unknown: 'bg-gray-100 dark:bg-gray-800',
  };

  const headerColor = headerColors[orderType] || headerColors.unknown;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg w-full min-w-[250px] max-w-full overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className={`${headerColor} px-4 py-3`}>
        <h3 className="font-cormorant text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

// Format Date Helper
const formatDate = (date: string | undefined | null): string => {
  if (!date || isNaN(new Date(date).getTime())) return 'N/A';
  return format(new Date(date), 'MMM dd, yyyy HH:mm');
};

// Calculate Total Helper
const calculateTotal = (items: { price: number; quantity: number }[] | string | null | undefined): number => {
  let parsedItems: { price: number; quantity: number }[] = [];
  if (typeof items === 'string') {
    try { parsedItems = JSON.parse(items); } catch { return 0; }
  } else if (Array.isArray(items)) {
    parsedItems = items;
  }
  return parsedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
};

// StatusBadge Component
type Status = 'pending' | 'completed' | 'cancelled' | 'in_progress' | 'confirmed';

interface StatusConfig {
  icon: LucideIcon;
  color: string;
}

const statusConfig: Record<Status, StatusConfig> = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' },
  completed: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200' },
  cancelled: { icon: XCircle, color: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200' },
  in_progress: { icon: RefreshCw, color: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200' },
  confirmed: { icon: CheckCircle, color: 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const normalizedStatus = (status.toLowerCase() in statusConfig ? status.toLowerCase() : 'pending') as Status;
  const config = statusConfig[normalizedStatus];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-inter font-medium ${config.color} flex-shrink-0 min-w-[100px] justify-center`}>
      <Icon className="w-4 h-4 mr-2" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ReservationCard Component
const ReservationCard: React.FC<{ data: Reservation; onCancel: (id: string) => void }> = ({ data, onCancel }) => {
  const isPending = data.status.toLowerCase() === 'pending';

  return (
    <Card title="Reservation" orderType="reservation">
      <div className="space-y-4">
        <div className="flex justify-between items-start flex-wrap gap-2">
          <p className="font-cormorant font-bold text-xl text-gray-900 dark:text-white truncate">{data.name}</p>
          <StatusBadge status={data.status} />
        </div>
        <div className="text-sm font-inter text-gray-600 dark:text-gray-300 space-y-2">
          <p className="flex items-center gap-2"><Mail className="w-5 h-5" /><span>{data.email || 'N/A'}</span></p>
          <p className="flex items-center gap-2"><Phone className="w-5 h-5" /><span>{data.contact}</span></p>
        </div>
        <div className="text-sm font-inter bg-gray-100 dark:bg-gray-800 p-3 rounded-lg space-y-2">
          <p><span className="font-medium">Date:</span> {formatDate(data.date)}</p>
          <p><span className="font-medium">Time:</span> {data.time}</p>
          <p><span className="font-medium">Guests:</span> {data.guests}</p>
        </div>
        {data.special_requests && (
          <div className="text-sm font-inter bg-yellow-50 dark:bg-yellow-800 p-3 rounded-lg">
            <span className="font-medium">Requests:</span> {data.special_requests}
          </div>
        )}
        {isPending && (
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => onCancel(data.id)} className="flex-1 px-4 py-2 border border-red-500 text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-800 text-sm font-inter min-w-[100px]">
              Cancel
            </button>
            <button className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter min-w-[100px]">
              Modify
            </button>
          </div>
        )}
        <p className="text-sm font-inter text-gray-500 dark:text-gray-400">Created: {formatDate(data.created_at)}</p>
      </div>
    </Card>
  );
};

// TakeawayOrderCard Component
const TakeawayOrderCard: React.FC<{ data: TakeawayOrder; onCancel: (id: string) => void }> = ({ data, onCancel }) => {
  const total = calculateTotal(data.menu_selections);
  const isPending = data.order_status.toLowerCase() === 'pending';
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

  const menuSelections = typeof data.menu_selections === 'string'
    ? JSON.parse(data.menu_selections || '[]')
    : Array.isArray(data.menu_selections)
      ? data.menu_selections
      : [];

  const handleModifySave = async (updatedOrder: TakeawayOrder) => {
    try {
      if (!data.id) throw new Error('Order ID is missing');
      const menuSelections = updatedOrder.menu_selections || [];
      if (menuSelections.length === 0) {
        await handleCancelOrder(data.id);
        return;
      }
      const { error } = await supabase
        .from('takeaway_orders')
        .update({
          menu_selections: menuSelections,
          pickup_time: updatedOrder.pickup_time,
          instructions: updatedOrder.instructions || null,
          address: updatedOrder.address,
          order_status: 'pending'
        })
        .eq('id', data.id);
      if (error) throw error;
      toast.success('Takeaway order modified successfully!');
    } catch (error: any) {
      console.error('Failed to modify takeaway order:', error);
      toast.error(`Failed to modify order: ${error.message}`);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('takeaway_orders')
        .update({ order_status: 'cancelled' })
        .eq('id', orderId);
      if (error) throw error;
      toast.success('Takeaway order cancelled successfully!');
      setIsModifyModalOpen(false);
    } catch (error: any) {
      toast.error(`Failed to cancel order: ${error.message}`);
    }
  };

  return (
    <>
      <Card title="Takeaway Order" orderType="takeaway">
        <div className="space-y-4">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <p className="font-cormorant font-bold text-xl text-gray-900 dark:text-white truncate">{data.name}</p>
            <StatusBadge status={data.order_status} />
          </div>
          <div className="text-sm font-inter text-gray-600 dark:text-gray-300 space-y-2">
            <p className="flex items-center gap-2"><Mail className="w-5 h-5" /><span>{data.email || 'N/A'}</span></p>
            <p className="flex items-center gap-2"><Phone className="w-5 h-5" /><span>{data.contact}</span></p>
          </div>
          <div className="text-sm font-inter bg-gray-100 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p><span className="font-medium">Pickup:</span> {formatDate(data.pickup_time)}</p>
            <p><span className="font-medium">Address:</span> {data.address}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-inter font-medium">Items:</p>
            {menuSelections.map((item: MenuItem, index: number) => (
              <div key={index} className="flex justify-between items-center text-sm font-inter py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <img src={item.image || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                  <span className="truncate">{item.name} x{item.quantity}</span>
                </div>
                <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <p className="text-sm font-inter font-medium">Total: <span className="text-emerald-600 dark:text-emerald-400">£{total.toFixed(2)}</span></p>
          </div>
          {data.instructions && (
            <div className="text-sm font-inter bg-yellow-50 dark:bg-yellow-800 p-3 rounded-lg">
              <span className="font-medium">Instructions:</span> {data.instructions}
            </div>
          )}
          {isPending && (
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => onCancel(data.id)} className="flex-1 px-4 py-2 border border-red-500 text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-800 text-sm font-inter min-w-[100px]">
                Cancel
              </button>
              <button
                onClick={() => setIsModifyModalOpen(true)}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter min-w-[100px]"
              >
                Modify
              </button>
            </div>
          )}
          <p className="text-sm font-inter text-gray-500 dark:text-gray-400">Created: {formatDate(data.created_at)}</p>
        </div>
      </Card>
      <ModifyTakeawayModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        order={data}
        onSave={handleModifySave}
        onCancelOrder={handleCancelOrder}
      />
    </>
  );
};

// ModifyTakeawayModal Component
interface TakeawayModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: TakeawayOrder;
  onSave: (updatedOrder: TakeawayOrder) => void;
  onCancelOrder: (orderId: string) => void;
}

const ModifyTakeawayModal: React.FC<TakeawayModalProps> = ({ isOpen, onClose, order, onSave, onCancelOrder }) => {
  const [modifiedItems, setModifiedItems] = useState<MenuItem[]>(order.menu_selections || []);
  const [pickupTime, setPickupTime] = useState(order.pickup_time);
  const [instructions, setInstructions] = useState(order.instructions || '');
  const [address, setAddress] = useState(order.address);
  const [availableMenuItems, setAvailableMenuItems] = useState<AvailableMenuItem[]>([]);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenuItems(true);
      try {
        const { data, error } = await supabase
          .from('menu_items')
          .select('id, name, price, image_url, category');
        if (error) throw error;
        setAvailableMenuItems(data || []);
      } catch (error: any) {
        toast.error(`Failed to load menu items: ${error.message}`);
      } finally {
        setLoadingMenuItems(false);
      }
    };

    if (isOpen) {
      fetchMenuItems();
    }
  }, [isOpen]);

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const newItems = [...modifiedItems];
    if (newQuantity <= 0) {
      newItems.splice(index, 1);
    } else {
      newItems[index] = { ...newItems[index], quantity: newQuantity };
    }
    setModifiedItems(newItems);
  };

  const handleAddItem = (newItem: AvailableMenuItem) => {
    const existingItem = modifiedItems.find(item => item.name === newItem.name);
    if (existingItem) {
      const newItems = modifiedItems.map(item =>
        item.name === newItem.name ? { ...item, quantity: item.quantity + 1 } : item
      );
      setModifiedItems(newItems);
    } else {
      setModifiedItems([...modifiedItems, {
        name: newItem.name,
        price: newItem.price,
        quantity: 1,
        image_url: newItem.image_url,
        category: newItem.category
      }]);
    }
  };

  const handleSave = () => {
    if (modifiedItems.length === 0) {
      setIsCancelConfirmOpen(true);
      return;
    }

    const updatedOrder: TakeawayOrder = {
      ...order,
      menu_selections: modifiedItems,
      pickup_time: pickupTime,
      instructions,
      address,
      order_status: 'pending'
    };
    onSave(updatedOrder);
    onClose();
  };

  const handleConfirmCancel = () => {
    onCancelOrder(order.id);
    setIsCancelConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-2xl transition-all">
                  <Dialog.Title as="h3" className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white mb-4">
                    Modify Takeaway Order
                  </Dialog.Title>

                  <div className="space-y-6">
                    {modifiedItems.length > 0 ? (
                      <div>
                        <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">Current Items</h4>
                        {modifiedItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                              <img src={item.image_url || item.image || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                              <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleQuantityChange(index, item.quantity - 1)}
                                className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                              >
                                -
                              </button>
                              <span className="text-sm text-gray-900 dark:text-white">{item.quantity}</span>
                              <button
                                onClick={() => handleQuantityChange(index, item.quantity + 1)}
                                className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div>
                      <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">
                        {modifiedItems.length > 0 ? 'Add More Items' : 'Select Items'}
                      </h4>
                      {loadingMenuItems ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading menu items...</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto">
                          {availableMenuItems.map((item) => (
                            <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-3">
                                <img src={item.image_url || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                                <div>
                                  <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">£{item.price.toFixed(2)}</p>
                                </div>
                              </div>
                              {!modifiedItems.some(modified => modified.name === item.name) && (
                                <button
                                  onClick={() => handleAddItem(item)}
                                  className="p-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700"
                                >
                                  <Plus className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Pickup Time</label>
                      <input
                        type="datetime-local"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Address</label>
                      <textarea
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={4}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Instructions</label>
                      <textarea
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={4}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-inter"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter"
                    >
                      Save Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <AlertDialog.Root open={isCancelConfirmOpen} onOpenChange={setIsCancelConfirmOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl max-w-md w-full">
            <AlertDialog.Title className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white">
              Cancel Order
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm font-inter text-gray-600 dark:text-gray-300 mt-3">
              No items are selected. Do you want to cancel this order?
            </AlertDialog.Description>
            <div className="mt-6 flex gap-3">
              <AlertDialog.Cancel asChild>
                <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-inter">
                  Keep Order
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={handleConfirmCancel}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 text-sm font-inter"
                >
                  Cancel Order
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
};

// PartyOrderCard Component
const PartyOrderCard: React.FC<{ data: PartyOrder; onCancel: (id: string) => void }> = ({ data, onCancel }) => {
  const total = calculateTotal(data.dish_selections);
  const isPending = data.status.toLowerCase() === 'pending';
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

  const dishSelections = typeof data.dish_selections === 'string'
    ? JSON.parse(data.dish_selections || '[]')
    : Array.isArray(data.dish_selections)
      ? data.dish_selections
      : [];

  const handleModifySave = async (updatedOrder: PartyOrder) => {
    try {
      if (!data.id) throw new Error('Order ID is missing');
      const dishSelections = updatedOrder.dish_selections || [];
      if (dishSelections.length === 0) {
        await handleCancelOrder(data.id);
        return;
      }
      const { error } = await supabase
        .from('party_orders')
        .update({
          dish_selections: dishSelections,
          event_date: updatedOrder.event_date,
          guest_count: updatedOrder.guest_count,
          delivery_method: updatedOrder.delivery_method,
          special_requests: updatedOrder.special_requests || null,
          address: updatedOrder.address,
          status: 'pending'
        })
        .eq('id', data.id);
      if (error) throw error;
      toast.success('Party order modified successfully!');
    } catch (error: any) {
      console.error('Failed to modify party order:', error);
      toast.error(`Failed to modify order: ${error.message}`);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('party_orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId);
      if (error) throw error;
      toast.success('Party order cancelled successfully!');
      setIsModifyModalOpen(false);
    } catch (error: any) {
      toast.error(`Failed to cancel order: ${error.message}`);
    }
  };

  return (
    <>
      <Card title="Party Order" orderType="party">
        <div className="space-y-4">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <p className="font-cormorant font-bold text-xl text-gray-900 dark:text-white truncate">{data.name}</p>
            <StatusBadge status={data.status} />
          </div>
          <div className="text-sm font-inter text-gray-600 dark:text-gray-300 space-y-2">
            <p className="flex items-center gap-2"><Mail className="w-5 h-5" /><span>{data.email || 'N/A'}</span></p>
            <p className="flex items-center gap-2"><Phone className="w-5 h-5" /><span>{data.contact}</span></p>
          </div>
          <div className="text-sm font-inter bg-gray-100 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p><span className="font-medium">Date:</span> {formatDate(data.event_date)}</p>
            <p><span className="font-medium">Guests:</span> {data.guest_count}</p>
            <p><span className="font-medium">Delivery:</span> {data.delivery_method}</p>
            <p><span className="font-medium">Address:</span> {data.address}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-inter font-medium">Dishes:</p>
            {dishSelections.map((item: MenuItem, index: number) => (
              <div key={index} className="flex justify-between items-center text-sm font-inter py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <img src={item.image || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                  <span className="truncate">{item.name} x{item.quantity}</span>
                </div>
                <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <p className="text-sm font-inter font-medium">Total: <span className="text-emerald-600 dark:text-emerald-400">£{total.toFixed(2)}</span></p>
          </div>
          {data.special_requests && (
            <div className="text-sm font-inter bg-yellow-50 dark:bg-yellow-800 p-3 rounded-lg">
              <span className="font-medium">Requests:</span> {data.special_requests}
            </div>
          )}
          {isPending && (
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => onCancel(data.id)} className="flex-1 px-4 py-2 border border-red-500 text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-800 text-sm font-inter min-w-[100px]">
                Cancel
              </button>
              <button
                onClick={() => setIsModifyModalOpen(true)}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter min-w-[100px]"
              >
                Modify
              </button>
            </div>
          )}
          <p className="text-sm font-inter text-gray-500 dark:text-gray-400">Created: {formatDate(data.created_at)}</p>
        </div>
      </Card>
      <ModifyPartyModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        order={data}
        onSave={handleModifySave}
        onCancelOrder={handleCancelOrder}
      />
    </>
  );
};

// ModifyPartyModal Component
interface PartyModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: PartyOrder;
  onSave: (updatedOrder: PartyOrder) => void;
  onCancelOrder: (orderId: string) => void;
}

const ModifyPartyModal: React.FC<PartyModalProps> = ({ isOpen, onClose, order, onSave, onCancelOrder }) => {
  const [modifiedItems, setModifiedItems] = useState<MenuItem[]>(order.dish_selections || []);
  const [eventDate, setEventDate] = useState(order.event_date);
  const [guestCount, setGuestCount] = useState(order.guest_count);
  const [deliveryMethod, setDeliveryMethod] = useState(order.delivery_method);
  const [specialRequests, setSpecialRequests] = useState(order.special_requests || '');
  const [address, setAddress] = useState(order.address);
  const [availableMenuItems, setAvailableMenuItems] = useState<AvailableMenuItem[]>([]);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenuItems(true);
      try {
        const { data, error } = await supabase
          .from('menu_items')
          .select('id, name, price, image_url, category');
        if (error) throw error;
        setAvailableMenuItems(data || []);
      } catch (error: any) {
        toast.error(`Failed to load menu items: ${error.message}`);
      } finally {
        setLoadingMenuItems(false);
      }
    };

    if (isOpen) {
      fetchMenuItems();
    }
  }, [isOpen]);

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const newItems = [...modifiedItems];
    if (newQuantity <= 0) {
      newItems.splice(index, 1);
    } else {
      newItems[index] = { ...newItems[index], quantity: newQuantity };
    }
    setModifiedItems(newItems);
  };

  const handleAddItem = (newItem: AvailableMenuItem) => {
    const existingItem = modifiedItems.find(item => item.name === newItem.name);
    if (existingItem) {
      const newItems = modifiedItems.map(item =>
        item.name === newItem.name ? { ...item, quantity: item.quantity + 1 } : item
      );
      setModifiedItems(newItems);
    } else {
      setModifiedItems([...modifiedItems, {
        name: newItem.name,
        price: newItem.price,
        quantity: 1,
        image_url: newItem.image_url,
        category: newItem.category
      }]);
    }
  };

  const handleSave = () => {
    if (modifiedItems.length === 0) {
      setIsCancelConfirmOpen(true);
      return;
    }

    const updatedOrder: PartyOrder = {
      ...order,
      dish_selections: modifiedItems,
      event_date: eventDate,
      guest_count: guestCount,
      delivery_method: deliveryMethod,
      special_requests: specialRequests,
      address,
      status: 'pending'
    };
    onSave(updatedOrder);
    onClose();
  };

  const handleConfirmCancel = () => {
    onCancelOrder(order.id);
    setIsCancelConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-2xl transition-all">
                  <Dialog.Title as="h3" className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white mb-4">
                    Modify Party Order
                  </Dialog.Title>

                  <div className="space-y-6">
                    {modifiedItems.length > 0 ? (
                      <div>
                        <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">Current Dishes</h4>
                        {modifiedItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                              <img src={item.image_url || item.image || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                              <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleQuantityChange(index, item.quantity - 1)}
                                className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                              >
                                -
                              </button>
                              <span className="text-sm text-gray-900 dark:text-white">{item.quantity}</span>
                              <button
                                onClick={() => handleQuantityChange(index, item.quantity + 1)}
                                className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div>
                      <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">
                        {modifiedItems.length > 0 ? 'Add More Dishes' : 'Select Dishes'}
                      </h4>
                      {loadingMenuItems ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading menu items...</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto">
                          {availableMenuItems.map((item) => (
                            <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-3">
                                <img src={item.image_url || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                                <div>
                                  <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">£{item.price.toFixed(2)}</p>
                                </div>
                              </div>
                              {!modifiedItems.some(modified => modified.name === item.name) && (
                                <button
                                  onClick={() => handleAddItem(item)}
                                  className="p-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700"
                                >
                                  <Plus className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Event Date</label>
                      <input
                        type="date"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Guest Count</label>
                      <input
                        type="number"
                        value={guestCount}
                        onChange={(e) => setGuestCount(Number(e.target.value))}
                        min="1"
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Delivery Method</label>
                      <select
                        value={deliveryMethod}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Delivery">Delivery</option>
                        <option value="Pickup">Pickup</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Address</label>
                      <textarea
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={4}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Special Requests</label>
                      <textarea
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={4}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-inter"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter"
                    >
                      Save Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <AlertDialog.Root open={isCancelConfirmOpen} onOpenChange={setIsCancelConfirmOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl max-w-md w-full">
            <AlertDialog.Title className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white">
              Cancel Order
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm font-inter text-gray-600 dark:text-gray-300 mt-3">
              No dishes are selected. Do you want to cancel this order?
            </AlertDialog.Description>
            <div className="mt-6 flex gap-3">
              <AlertDialog.Cancel asChild>
                <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-inter">
                  Keep Order
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={handleConfirmCancel}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 text-sm font-inter"
                >
                  Cancel Order
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
};

// MenuOrderCard Component
interface MenuOrderCardProps {
  data: MenuDetails;
  onCancel: (id: string) => void;
  profile: Profile | null;
}

const MenuOrderCard: React.FC<MenuOrderCardProps> = ({ data, onCancel, profile }) => {
  const isPending = (data.status || 'unknown').toLowerCase() === 'pending';
  const shippingAddress = data.shipping_info?.address || profile?.address || 'clg, home';
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

  const handleModifySave = async (updatedOrder: MenuDetails) => {
    try {
      if (!data.id) throw new Error('Order ID is missing');
      if (updatedOrder.items.length === 0) {
        await handleCancelOrder(data.id);
        return;
      }
      const { error } = await supabase
        .from('menuorder')
        .update({
          items: updatedOrder.items,
          total_amount: updatedOrder.total_amount,
          shipping_info: updatedOrder.shipping_info,
        })
        .eq('id', data.id);
      if (error) throw error;
      toast.success('Order modified successfully!');
    } catch (error: any) {
      toast.error(`Failed to modify order: ${error.message}`);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('menuorder')
        .update({ status: 'cancelled' })
        .eq('id', orderId);
      if (error) throw error;
      toast.success('Order cancelled successfully!');
      setIsModifyModalOpen(false);
    } catch (error: any) {
      toast.error(`Failed to cancel order: ${error.message}`);
    }
  };

  return (
    <>
      <Card title="Menu Order" orderType="menu">
        <div className="space-y-4">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <div className="text-sm font-inter text-gray-600 dark:text-gray-300 space-y-2">
                <p className="flex items-center gap-2"><Mail className="w-5 h-5" /><span>{profile?.email || 'N/A'}</span></p>
                <p className="flex items-center gap-2"><Phone className="w-5 h-5" /><span>{profile?.phone || '9876553323'}</span></p>
              </div>
            </div>
            <StatusBadge status={data.status || 'Unknown'} />
          </div>
          <div className="text-sm font-inter bg-gray-100 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p><span className="font-medium text-gray-900 dark:text-gray-200">Payment:</span> <span className="text-gray-700 dark:text-gray-300">{data.payment_method || 'Cash on Delivery'}</span></p>
            <p><span className="font-medium text-gray-900 dark:text-gray-200">Shipping:</span> <span className="text-gray-700 dark:text-gray-300">{shippingAddress}</span></p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-inter font-medium text-gray-900 dark:text-gray-200">Items:</p>
            {(data.items || []).map((item, index) => (
              <div key={index} className="flex justify-between items-center text-sm font-inter py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <img src={item.image_url || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                  <span className="truncate text-gray-900 dark:text-gray-200">{item.name} x{item.quantity}</span>
                </div>
                <span className="text-gray-900 dark:text-gray-200">£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <p className="text-sm font-inter font-medium text-gray-900 dark:text-gray-200">
              Total: <span className="text-emerald-600 dark:text-emerald-400">£{data.total_amount.toFixed(2)}</span>
            </p>
          </div>
          {isPending && (
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => data.id && onCancel(data.id)} className="flex-1 px-4 py-2 border border-red-500 text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-800 text-sm font-inter min-w-[100px]">
                Cancel
              </button>
              <button
                onClick={() => setIsModifyModalOpen(true)}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter min-w-[100px]"
              >
                Modify
              </button>
            </div>
          )}
          <p className="text-sm font-inter text-gray-500 dark:text-gray-400">Created: {formatDate(data.created_at)}</p>
        </div>
      </Card>
      <ModifyOrderModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        order={data}
        onSave={handleModifySave}
        onCancelOrder={handleCancelOrder}
      />
    </>
  );
};

// ModifyOrderModal Component
const ModifyOrderModal: React.FC<ModifyModalProps> = ({ isOpen, onClose, order, onSave, onCancelOrder }) => {
  const [modifiedItems, setModifiedItems] = useState<MenuItem[]>(order.items);
  const [shippingAddress, setShippingAddress] = useState(order.shipping_info?.address || '');
  const [availableMenuItems, setAvailableMenuItems] = useState<AvailableMenuItem[]>([]);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenuItems(true);
      try {
        const { data, error } = await supabase
          .from('menu_items')
          .select('id, name, price, image_url');
        if (error) throw error;
        setAvailableMenuItems(data || []);
      } catch (error: any) {
        toast.error(`Failed to load menu items: ${error.message}`);
      } finally {
        setLoadingMenuItems(false);
      }
    };

    if (isOpen) {
      fetchMenuItems();
    }
  }, [isOpen]);

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const newItems = [...modifiedItems];
    if (newQuantity <= 0) {
      newItems.splice(index, 1);
    } else {
      newItems[index] = { ...newItems[index], quantity: newQuantity };
    }
    setModifiedItems(newItems);
  };

  const handleAddItem = (newItem: AvailableMenuItem) => {
    const existingItem = modifiedItems.find(item => item.name === newItem.name);
    if (existingItem) {
      const newItems = modifiedItems.map(item =>
        item.name === newItem.name ? { ...item, quantity: item.quantity + 1 } : item
      );
      setModifiedItems(newItems);
    } else {
      setModifiedItems([...modifiedItems, {
        name: newItem.name,
        price: newItem.price,
        quantity: 1,
        image_url: newItem.image_url
      }]);
    }
  };

  const handleSave = () => {
    if (modifiedItems.length === 0 && order.id) {
      onCancelOrder(order.id);
    } else {
      const updatedOrder: MenuDetails = {
        ...order,
        items: modifiedItems,
        total_amount: modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        shipping_info: { address: shippingAddress }
      };
      onSave(updatedOrder);
    }
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-2xl transition-all">
                <Dialog.Title as="h3" className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white mb-4">
                  Modify Order
                </Dialog.Title>

                <div className="space-y-6">
                  {modifiedItems.length > 0 ? (
                    <div>
                      <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">Current Items</h4>
                      {modifiedItems.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-3">
                            <img src={item.image_url || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                            <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleQuantityChange(index, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                            >
                              -
                            </button>
                            <span className="text-sm text-gray-900 dark:text-white">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(index, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full text-sm"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div>
                    <h4 className="text-base font-inter font-medium text-gray-700 dark:text-gray-200 mb-3">
                      {modifiedItems.length > 0 ? 'Add More Items' : 'Select Items'}
                    </h4>
                    {loadingMenuItems ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading menu items...</p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {availableMenuItems.map((item) => (
                          <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                              <img src={item.image_url || 'https://via.placeholder.com/40'} alt={item.name} className="w-10 h-10 object-cover rounded-md" />
                              <div>
                                <span className="text-sm text-gray-900 dark:text-white">{item.name}</span>
                                <p className="text-sm text-gray-600 dark:text-gray-400">£{item.price.toFixed(2)}</p>
                              </div>
                            </div>
                            {!modifiedItems.some(modified => modified.name === item.name) && (
                              <button
                                onClick={() => handleAddItem(item)}
                                className="p-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Shipping Address</label>
                    <textarea
                      value={shippingAddress}
                      onChange={(e) => setShippingAddress(e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      rows={4}
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-inter"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={modifiedItems.length === 0 && !order.id}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-inter"
                  >
                    {modifiedItems.length === 0 ? 'Cancel Order' : 'Save Changes'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// FilterBar Component
const FilterBar: React.FC<{ typeFilter: string; setTypeFilter: (filter: string) => void; statusFilter: string; setStatusFilter: (filter: string) => void }> = ({ typeFilter, setTypeFilter, statusFilter, setStatusFilter }) => {
  const typeOptions = ['All', 'Menu', 'Takeaway', 'Party Order', 'Dine In'];
  const statusOptions = ['All', 'Pending', 'Confirmed', 'Completed', 'Cancelled'];

  return (
    <div className="mb-6 w-full">
      <div className="flex flex-col gap-4">
        <div className="w-full">
          <label htmlFor="typeFilter" className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Order Type:</label>
          <select
            id="typeFilter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value.toLowerCase().replace(' ', ''))}
            className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-inter dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {typeOptions.map((option) => (
              <option key={option} value={option.toLowerCase().replace(' ', '')}>{option}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg justify-center">
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status.toLowerCase())}
              className={`px-4 py-2 rounded-full text-sm font-inter font-medium transition-colors ${statusFilter === status.toLowerCase()
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Sidebar Component
interface SidebarProps {
  user: Profile | null;
  signOut: () => Promise<void>;
  setActiveTab: Dispatch<SetStateAction<'overview' | 'orders' | 'addresses' | 'settings'>>;
  activeTab: 'overview' | 'orders' | 'addresses' | 'settings';
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

function Sidebar({ user, signOut, setActiveTab, activeTab, isOpen, setIsOpen }: SidebarProps) {
  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setIsOpen(false)}></div>}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setIsOpen(false)} className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
              <X className="w-5 h-5 text-gray-600 dark:text-gray-200" />
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-emerald-600 dark:bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <User className="w-6 h-6 text-white" />
            </div>
            <p className="font-cormorant font-semibold text-xl text-gray-900 dark:text-white truncate">{user?.full_name || 'User'}</p>
            <p className="text-sm font-inter text-gray-600 dark:text-gray-400 truncate">{user?.email || 'N/A'}</p>
          </div>
          <nav className="space-y-2 flex-1">
            {['overview', 'orders', 'addresses', 'settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab as any); setIsOpen(false); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-sm font-inter ${activeTab === tab ? 'bg-emerald-600 text-white' : 'text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
              >
                {tab === 'overview' && <Home className="w-5 h-5" />}
                {tab === 'orders' && <ShoppingBag className="w-5 h-5" />}
                {tab === 'addresses' && <MapPin className="w-5 h-5" />}
                {tab === 'settings' && <Settings className="w-5 h-5" />}
                <span>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
              </button>
            ))}
          </nav>
          <button
            onClick={() => { signOut(); setIsOpen(false); }}
            className="w-full flex items-center gap-3 p-3 text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-sm font-inter"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}

// Header Component
function Header({ setIsSidebarOpen }: { setIsSidebarOpen: Dispatch<SetStateAction<boolean>> }) {
  return (
    <header className="bg-white dark:bg-gray-900 shadow-lg py-4 px-6 flex justify-between items-center sticky top-0 z-30 w-full">
      <div className="flex items-center gap-3">
        <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
          <Menu className="w-6 h-6 text-gray-600 dark:text-gray-200" />
        </button>
        <h1 className="font-display text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-0 ml-6 sm:mb-6 text-center lg:text-left">User Dashboard</h1>
      </div>
      <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 relative">
        <Bell className="w-6 h-6 text-gray-600 dark:text-gray-200" />
        <span className="absolute top-0 right-0 w-3 h-3 bg-red-600 rounded-full"></span>
      </button>
    </header>
  );
}

// StatCard Component
function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-lg w-full">
      <p className="text-gray-600 dark:text-gray-300 text-sm font-inter truncate">{title}</p>
      <h3 className="text-xl font-cormorant font-bold text-gray-900 dark:text-white truncate">{value}</h3>
    </div>
  );
}

// ProfileCompletionModal Component
const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [fullName, setFullName] = useState(initialData.full_name);
  const [address, setAddress] = useState(initialData.address);
  const [phone, setPhone] = useState(initialData.phone);

  const handleSave = () => {
    if (!fullName || !address || !phone) {
      toast.error('Please fill in all fields.');
      return;
    }
    onSave({ full_name: fullName, address, phone });
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => { }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-2xl transition-all">
                <Dialog.Title as="h3" className="text-2xl font-cormorant font-semibold text-gray-900 dark:text-white mb-4">
                  Complete Your Profile
                </Dialog.Title>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Shipping Address</label>
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      rows={4}
                      placeholder="Enter your shipping address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-inter font-medium text-gray-700 dark:text-gray-200 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 text-sm font-inter"
                  >
                    Save Profile
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// UserDashboard Component
const UserDashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'addresses' | 'settings'>('overview');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [address, setAddress] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        if (!user?.id) throw new Error('User not authenticated');
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, email, created_at, role, phone, address, updated_at')
          .eq('id', user.id)
          .single();
        if (profileError) throw profileError;
        setProfile(profileData);
        setAddress(profileData.address || '');
        setFullName(profileData.full_name || '');
        setEmail(profileData.email || '');
        setPhone(profileData.phone || '');
        setIsProfileModalOpen(!profileData.full_name || !profileData.address || !profileData.phone);

        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            id,
            user_id,
            typeoforder,
            created_at,
            reservation_id,
            party_order_id,
            takeaway_order_id,
            menuorder_id,
            menuorder:menuorder_id (
              id,
              user_id,
              items,
              total_amount,
  
                shipping_info,
                payment_method,
                status,
                created_at
              )
            `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (ordersError) throw ordersError;

        const detailedOrders = await Promise.all(
          (ordersData || []).map(async (order) => {
            let details: any = {};
            let userEmail: string | undefined;
            if (order.user_id) {
              const { data: profileData } = await supabase.from('profiles').select('email').eq('id', order.user_id).single();
              userEmail = profileData?.email;
            }
            if (order.typeoforder === 'reservation' && order.reservation_id) {
              const { data } = await supabase.from('reservations').select('*').eq('id', order.reservation_id).single();
              details = { ...data, email: userEmail || 'N/A' };
            } else if (order.typeoforder === 'party_order' && order.party_order_id) {
              const { data } = await supabase.from('party_orders').select('*').eq('id', order.party_order_id).single();
              details = { ...data, email: userEmail || 'N/A' };
              if (details.dish_selections && typeof details.dish_selections === 'string') {
                details.dish_selections = JSON.parse(details.dish_selections) || [];
              }
              if (details.dish_selections && Array.isArray(details.dish_selections)) {
                details.dish_selections = await Promise.all(
                  details.dish_selections.map(async (item: MenuItem) => {
                    const { data: menuItem } = await supabase.from('menu_items').select('image_url').eq('name', item.name).single();
                    return { ...item, image_url: menuItem?.image_url || '' };
                  })
                );
              }
            } else if (order.typeoforder === 'takeaway_order' && order.takeaway_order_id) {
              const { data } = await supabase.from('takeaway_orders').select('*').eq('id', order.takeaway_order_id).single();
              details = { ...data, email: userEmail || 'N/A' };
              if (details.menu_selections && typeof details.menu_selections === 'string') {
                details.menu_selections = JSON.parse(details.menu_selections) || [];
              }
              if (details.menu_selections && Array.isArray(details.menu_selections)) {
                details.menu_selections = await Promise.all(
                  details.menu_selections.map(async (item: MenuItem) => {
                    const { data: menuItem } = await supabase.from('menu_items').select('image_url').eq('name', item.name).single();
                    return { ...item, image_url: menuItem?.image_url || '' };
                  })
                );
              }
            } else if (order.typeoforder === 'dine_in' && order.reservation_id) {
              const { data } = await supabase.from('reservations').select('*').eq('id', order.reservation_id).single();
              details = { ...data, email: userEmail || 'N/A' };
            }

            const menuOrderData = Array.isArray(order.menuorder) ? order.menuorder[0] : order.menuorder;
            let enrichedMenuDetails;
            if (menuOrderData) {
              let items = Array.isArray(menuOrderData.items) ? menuOrderData.items : JSON.parse(menuOrderData.items || '[]');
              items = await Promise.all(
                items.map(async (item: MenuItem) => {
                  const { data: menuItem } = await supabase.from('menu_items').select('image_url').eq('name', item.name).single();
                  return { ...item, image_url: menuItem?.image_url || '' };
                })
              );
              enrichedMenuDetails = {
                id: menuOrderData.id,
                user_id: menuOrderData.user_id,
                items,
                total_amount: Number(menuOrderData.total_amount) || 0,
                shipping_info: menuOrderData.shipping_info || {},
                payment_method: menuOrderData.payment_method || 'N/A',
                status: menuOrderData.status || 'Unknown',
                created_at: menuOrderData.created_at,
              };
            }

            return {
              id: order.id,
              typeoforder: order.typeoforder || (enrichedMenuDetails ? 'menu' : undefined),
              created_at: order.created_at,
              menuorder_id: order.menuorder_id,
              user_id: order.user_id,
              details,
              menuDetails: enrichedMenuDetails,
            };
          })
        );

        setOrders(detailedOrders);
      } catch (error: any) {
        toast.error(`Failed to load user data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleSaveProfile = async (updatedProfile: { full_name: string; address: string; phone: string }) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: updatedProfile.full_name,
          address: updatedProfile.address,
          phone: updatedProfile.phone,
        })
        .eq('id', user?.id);
      if (error) throw error;

      setProfile((prev) =>
        prev
          ? {
            ...prev,
            full_name: updatedProfile.full_name,
            address: updatedProfile.address,
            phone: updatedProfile.phone,
          }
          : null
      );
      setFullName(updatedProfile.full_name);
      setAddress(updatedProfile.address);
      setPhone(updatedProfile.phone);
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      toast.error(`Failed to update profile: ${error.message}`);
    }
  };

  const handleCancel = async (orderId: string) => {
  try {
    const safeOrderId = orderId.toString().trim().toLowerCase();
    let { data: orderData, error: fetchError } = await supabase
      .from('orders')
      .select('id, menuorder_id, typeoforder, reservation_id, party_order_id, takeaway_order_id, user_id')
      .eq('id', safeOrderId)
      .limit(1);
    if (fetchError) throw fetchError;

    if (!orderData || orderData.length === 0) {
      const { data: ordersData, error: ordersFetchError } = await supabase
        .from('orders')
        .select('id, menuorder_id, typeoforder, reservation_id, party_order_id, takeaway_order_id, user_id')
        .eq('menuorder_id', safeOrderId)
        .limit(1);
      if (ordersFetchError) throw ordersFetchError;
      if (!ordersData || ordersData.length === 0) {
        toast.error(`No order found with id: ${safeOrderId}`);
        return;
      }
      orderData = ordersData;
    }

    const order = orderData[0];
    let menuStatus: string | undefined;
    let reservationStatus: string | undefined;
    let partyStatus: string | undefined;
    let takeawayStatus: string | undefined;

    if (order?.menuorder_id) {
      const { data: menuOrder, error: menuError } = await supabase.from('menuorder').select('status').eq('id', order.menuorder_id).single();
      if (menuError) throw menuError;
      menuStatus = menuOrder?.status;
    } else if (order?.typeoforder === 'reservation' && order.reservation_id) {
      const { data: reservation, error: resError } = await supabase.from('reservations').select('status').eq('id', order.reservation_id).single();
      if (resError) throw resError;
      reservationStatus = reservation?.status;
    } else if (order?.typeoforder === 'party_order' && order.party_order_id) {
      const { data: partyOrder, error: partyError } = await supabase.from('party_orders').select('status').eq('id', order.party_order_id).single();
      if (partyError) throw partyError;
      partyStatus = partyOrder?.status;
    } else if (order?.typeoforder === 'takeaway_order' && order.takeaway_order_id) {
      const { data: takeawayOrder, error: takeawayError } = await supabase.from('takeaway_orders').select('order_status').eq('id', order.takeaway_order_id).single();
      if (takeawayError) throw takeawayError;
      takeawayStatus = takeawayOrder?.order_status;
    }

    const currentStatus = menuStatus || reservationStatus || partyStatus || takeawayStatus;
    if (!currentStatus || currentStatus.toLowerCase() !== 'pending') {
      toast.error('Can only cancel orders that are pending.');
      return;
    }

    if (order?.menuorder_id) {
      const { error } = await supabase.from('menuorder').update({ status: 'cancelled' }).eq('id', order.menuorder_id);
      if (error) throw error;
      setOrders((prev) =>
        prev.map((orderItem) =>
          orderItem.id === order.id && orderItem.menuDetails
            ? { ...orderItem, menuDetails: { ...orderItem.menuDetails, status: 'cancelled' } }
            : orderItem
        )
      );
    } else if (order?.typeoforder === 'reservation' && order.reservation_id) {
      const { error } = await supabase.from('reservations').update({ status: 'cancelled' }).eq('id', order.reservation_id);
      if (error) throw error;
      setOrders((prev) =>
        prev.map((orderItem) =>
          orderItem.id === order.id && orderItem.details
            ? { ...orderItem, details: { ...orderItem.details, status: 'cancelled' } }
            : orderItem
        )
      );
    } else if (order?.typeoforder === 'party_order' && order.party_order_id) {
      const { error } = await supabase.from('party_orders').update({ status: 'cancelled' }).eq('id', order.party_order_id);
      if (error) throw error;
      setOrders((prev) =>
        prev.map((orderItem) =>
          orderItem.id === order.id && orderItem.details
            ? { ...orderItem, details: { ...orderItem.details, status: 'cancelled' } }
            : orderItem
        )
      );
    } else if (order?.typeoforder === 'takeaway_order' && order.takeaway_order_id) {
      const { error } = await supabase.from('takeaway_orders').update({ order_status: 'cancelled' }).eq('id', order.takeaway_order_id);
      if (error) throw error;
      setOrders((prev) =>
        prev.map((orderItem) =>
          orderItem.id === order.id && orderItem.details
            ? { ...orderItem, details: { ...orderItem.details, order_status: 'cancelled' } }
            : orderItem
        )
      );
    }
    toast.success('Order cancelled successfully!');
  } catch (error: any) {
    toast.error(`Failed to cancel order: ${error.message}`);
  }
};

  const handlePlaceNewOrder = () => navigate('/menu');

  const handleUpdateAddress = async () => {
    try {
      const { error } = await supabase.from('profiles').update({ address }).eq('id', user?.id);
      if (error) throw error;
      setProfile((prev) => prev ? { ...prev, address } : null);
      toast.success('Address updated successfully!');
    } catch (error: any) {
      toast.error(`Failed to update address: ${error.message}`);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const { error } = await supabase.from('profiles').update({ full_name: fullName, email, phone }).eq('id', user?.id);
      if (error) throw error;
      setProfile((prev) => prev ? { ...prev, full_name: fullName, email, phone } : null);
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      toast.error(`Failed to update profile: ${error.message}`);
    }
  };

  const totalOrders = orders.length.toString();
  const totalReservations = orders.filter((order) => order.typeoforder === 'reservation').length.toString();
  const totalTakeaways = orders.filter((order) => order.typeoforder === 'takeaway_order').length.toString();
  const totalPartyOrders = orders.filter((order) => order.typeoforder === 'party_order').length.toString();
  const totalMenuOrders = orders.filter((order) => order.menuDetails).length.toString();

  const filteredOrders = orders.filter((order) => {
    const normalizedType = order.typeoforder?.toLowerCase().replace('_', '') || '';
    const isMenuOrder = !!order.menuDetails;
    const isTakeawayOrder = order.typeoforder === 'takeaway_order';
    const orderTypeMatch =
      typeFilter === 'all' ||
      (typeFilter === 'menu' && isMenuOrder) ||
      (typeFilter === 'takeaway' && isTakeawayOrder) ||
      (typeFilter === 'partyorder' && normalizedType === 'partyorder') ||
      (typeFilter === 'dinein' && normalizedType === 'reservation');
    const status = order.menuDetails?.status?.toLowerCase() || order.details?.status?.toLowerCase() || order.details?.order_status?.toLowerCase() || 'unknown';
    const statusMatch =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && (status === 'pending' || status === 'in_progress')) ||
      (statusFilter === 'completed' && status === 'completed') ||
      (statusFilter === 'confirmed' && status === 'confirmed') ||
      (statusFilter === 'cancelled' && status === 'cancelled');
    return orderTypeMatch && statusMatch;
  });

  if (loading) {
    return <SkeletonUserDashboard activeTab={activeTab} />;
  }

  const renderOrderCard = (order: Order) => {
    if (order.typeoforder === 'reservation' && order.details) {
      return <ReservationCard key={order.id} data={{ ...order.details, id: order.id, created_at: order.created_at || '' }} onCancel={handleCancel} />;
    }
    if (order.typeoforder === 'takeaway_order' && order.details) {
      return <TakeawayOrderCard key={order.id} data={{ ...order.details, id: order.id, created_at: order.created_at || '' }} onCancel={handleCancel} />;
    }
    if (order.typeoforder === 'party_order' && order.details) {
      return <PartyOrderCard key={order.id} data={{ ...order.details, id: order.id, created_at: order.created_at || '' }} onCancel={handleCancel} />;
    }
    if (order.menuDetails) {
      return <MenuOrderCard key={order.id} data={order.menuDetails} onCancel={handleCancel} profile={profile} />;
    }
    return (
      <Card key={order.id} title="Unknown Order" orderType="unknown">
        <p className="text-gray-600 dark:text-gray-300 text-xs font-inter">Order type not recognized.</p>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 flex flex-col lg:flex-row overflow-x-hidden">
      <Sidebar user={profile} signOut={signOut} setActiveTab={setActiveTab} activeTab={activeTab} isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div className="flex-1 flex flex-col w-full">
        <Header setIsSidebarOpen={setIsSidebarOpen} />
        <main className="flex-1 py-8 sm:py-12 px-4 sm:px-6 lg:px-8 w-full overflow-x-auto">
          <div className="max-w-full sm:max-w-7xl mx-auto">
            {loading ? (
              <SkeletonUserDashboard activeTab={activeTab} />
            ) : (
              <>
                <ProfileCompletionModal
                  isOpen={isProfileModalOpen}
                  onClose={() => setIsProfileModalOpen(false)}
                  onSave={handleSaveProfile}
                  initialData={{ full_name: fullName, address, phone }}
                />
                {activeTab === 'overview' && (
                  <>
                    <div className="flex justify-between items-center mb-8 sm:mb-12 flex-col gap-3 sm:flex-row">
                      <h1 className="font-cormorant text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white truncate">Account Overview</h1>
                      <button
                        onClick={handlePlaceNewOrder}
                        className="inline-flex items-center justify-center space-x-2 bg-emerald-600 text-white font-inter text-sm sm:text-base font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-full hover:bg-emerald-700 transition-colors duration-300 w-full sm:w-auto"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        <span>Place New Order</span>
                      </button>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6 w-full">
                      <h2 className="font-cormorant text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">User Information</h2>
                      <div className="text-xs font-inter text-gray-600 dark:text-gray-300 space-y-2">
                        <p><span className="font-medium">Name:</span> {profile?.full_name || 'N/A'}</p>
                        <p><span className="font-medium">Phone:</span> {profile?.phone || 'N/A'}</p>
                        <p><span className="font-medium">Email:</span> {profile?.email || 'N/A'}</p>
                        <p><span className="font-medium">Address:</span> {profile?.address || 'N/A'}</p>
                        <p><span className="font-medium">Since:</span> {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-4 sm:mb-6 w-full">
                      <StatCard title="Total Orders" value={totalOrders} />
                      <StatCard title="Reservations" value={totalReservations} />
                      <StatCard title="Takeaways" value={totalTakeaways} />
                      <StatCard title="Party Orders" value={totalPartyOrders} />
                      <StatCard title="Menu Orders" value={totalMenuOrders} />
                    </div>
                    <div className="space-y-4 sm:space-y-6 w-full">
                      <h2 className="font-cormorant text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
                        {orders.length > 0 ? orders.slice(0, 3).map(renderOrderCard) : (
                          <p className="text-gray-600 dark:text-gray-300 text-sm font-inter col-span-full">No recent orders.</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {activeTab === 'orders' && (
                  <>
                    <div className="flex justify-between items-center mb-8 sm:mb-12 flex-col gap-3 sm:flex-row">
                      <h1 className="font-cormorant text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white truncate">All Orders</h1>
                      <button
                        onClick={handlePlaceNewOrder}
                        className="inline-flex items-center justify-center space-x-2 bg-emerald-600 text-white font-inter text-sm sm:text-base font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-full hover:bg-emerald-700 transition-colors duration-300 w-full sm:w-auto"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        <span>Place New Order</span>
                      </button>
                    </div>
                    <FilterBar typeFilter={typeFilter} setTypeFilter={setTypeFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
                      {filteredOrders.length > 0 ? filteredOrders.map(renderOrderCard) : (
                        <p className="text-gray-600 dark:text-gray-300 text-sm font-inter col-span-full">No orders match this filter.</p>
                      )}
                    </div>
                  </>
                )}
                {activeTab === 'addresses' && (
                  <div className="w-full max-w-md mx-auto">
                    <h1 className="font-cormorant text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 truncate">Manage Address</h1>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-xl shadow-sm">
                        <p className="text-xs font-inter font-medium text-gray-700 dark:text-gray-300">Current Address:</p>
                        <p className="text-gray-900 dark:text-white text-sm font-inter break-words">{profile?.address || 'No address set'}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-inter font-medium text-gray-700 dark:text-gray-300">Edit Address</label>
                        <textarea
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder="Enter your address"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-inter dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                          rows={3}
                        />
                        <button
                          onClick={handleUpdateAddress}
                          className="w-full bg-emerald-600 text-white font-inter text-sm sm:text-base font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-full hover:bg-emerald-700 transition-colors duration-300"
                        >
                          Update Address
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {activeTab === 'settings' && (
                  <div className="w-full max-w-md mx-auto">
                    <h1 className="font-cormorant text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 truncate">Profile Settings</h1>
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-xs font-inter font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-inter dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-inter font-medium text-gray-700 dark:text-gray-300">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-inter dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Enter your email"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-inter font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-inter dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Enter your phone number"
                          maxLength={10}
                        />
                      </div>
                      <button
                        onClick={handleUpdateProfile}
                        className="w-full bg-emerald-600 text-white font-inter text-sm sm:text-base font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-full hover:bg-emerald-700 transition-colors duration-300"
                      >
                        Update Profile
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <style>{`
          html {
            scroll-behavior: smooth;
          }
          main {
            -webkit-overflow-scrolling: touch;
          }
          @media (max-width: 640px) {
            .max-w-full {
              padding-left: 0.5rem;
              padding-right: 0.5rem;
            }
          }
        `}</style>
    </div>
  );
};


export default UserDashboard;